import React, { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import BasePersonTimelineRecordLayout from 'domain/shared/timeline/person/PersonTimelineRecord/BasePersonTimelineRecordLayout';
import { useShowContext } from 'framework/show';
import { TimelineType } from 'domain/shared/timeline/layout/BaseTimelineRecordLayout/timelineLayoutHelper';
import { Collapsible, HtmlField } from 'framework/ui/field';
import { ReplaceUrlWithLink, useAsyncValue, useSubmit } from 'framework/utils';
import { EmployeeLinkFieldWithAccessCheck } from 'domain/shared/employee';
import { DATE_FORMAT } from 'framework/utils';
import {
  personTimelineActionNameSx,
  personTimelineDateSx,
  personTimelineHeaderSx,
} from 'domain/shared/timeline/person/PersonTimelineRecord/personTimelineRecordSx';
import { PROJECT_NOTE_TIMELINE_ICONS, PROJECT_TIMELINE_MESSAGE_CONTEXT } from '../../timeline/constants';
import useRiskNotesService from '../useRiskNotesService';
import { useProjectId } from 'domain/shared/project';
import { useParams } from 'react-router-dom';
import { TimelineContextData } from 'framework/ui/timeline';
import DeleteConfirmationModal from '../../timeline/shared/DeleteConfirmationModal';
import { Box } from '@mui/material';
import { useListContext } from 'framework/list';
import { ShowDate } from 'framework/ui/ui-builder';
import { PinTimelineButton } from 'domain/shared/timeline';
import useAuthenticatedUserService from 'infrastructure/security/authentication/authenticatedUserService';

const RiskNoteTimelineRecordHeader = () => {
  const { data: entity } = useShowContext();

  return (
    <Box sx={personTimelineHeaderSx}>
      <Box sx={personTimelineActionNameSx}>
        <EmployeeLinkFieldWithAccessCheck value={entity?.updatedAt ? entity?.updatedBy : entity?.createdBy} />
      </Box>
      <Box sx={personTimelineDateSx}>
        <ShowDate dateUTC={entity?.updatedAt ? entity?.updatedAt : entity?.createdAt} format={DATE_FORMAT.FULL} />
      </Box>
    </Box>
  );
};

const RiskNoteTimelineRecordLayout = (props: Record<string, unknown>) => {
  const { data } = useShowContext();
  const entity = data.getData();
  const isPinned = data.getValueBySource('isPinned');

  const { getUserAlias } = useAuthenticatedUserService();
  const currentUserAlias = useAsyncValue(() => getUserAlias());
  const isAuthor = entity.createdBy === currentUserAlias;

  const { deleteRiskNote } = useRiskNotesService();
  const projectId = useProjectId();
  const { riskId } = useParams();

  const { deleteFromList, pinTimelineAction }: TimelineContextData = useListContext();

  const [modalOpen, setModalOpen] = useState(false);
  const { onSubmit, isLoading } = useSubmit({
    submitMethod: async () => {
      await deleteRiskNote(projectId, riskId!, entity.id);
      return {};
    },
    afterSubmit: { execute: () => (deleteFromList ? deleteFromList(entity.timelineId) : null) },
  });

  const buttons = [
    isAuthor && (
      <MenuItem key="edit" onClick={props.onEdit as React.MouseEventHandler<HTMLLIElement>}>
        Edit note
      </MenuItem>
    ),
    isAuthor && (
      <MenuItem key="delete" onClick={() => setModalOpen(true)}>
        Delete note
      </MenuItem>
    ),
  ];

  return (
    <>
      <DeleteConfirmationModal
        isOpen={modalOpen}
        isLoading={isLoading}
        message="Do you really want to delete risk timeline note?"
        onConfirm={onSubmit}
        onCancel={() => setModalOpen(false)}
      />
      <BasePersonTimelineRecordLayout
        actionName=""
        timelineType={TimelineType.NOTE}
        buttons={buttons}
        actions={[
          <Box sx={{ mr: 1 }}>
            <PinTimelineButton
              sx={{ fontSize: '15px' }}
              key="pin"
              pinStatus={isPinned}
              pinMethod={async () => {
                if (pinTimelineAction) {
                  await pinTimelineAction(projectId, entity.timelineId, !isPinned);
                  return;
                }
              }}
            />
          </Box>,
        ]}
        timelineIcons={PROJECT_NOTE_TIMELINE_ICONS}
        timelineHeader={RiskNoteTimelineRecordHeader}
        artifactsSource="payload.attachmentPaths"
        noteTextSource="payload.text"
        isRiskService={true}
        fields={
          <Collapsible>
            <ReplaceUrlWithLink>
              <Box sx={{ pb: 1 }}>
                <HtmlField source="payload.text" label="" />
              </Box>
            </ReplaceUrlWithLink>
          </Collapsible>
        }
        permissionToCheck={null}
        isCandidateService={false}
        timelineFooter={() => null}
        isShowButtons={() => true}
        {...props}
        messageContext={PROJECT_TIMELINE_MESSAGE_CONTEXT}
      />
    </>
  );
};

export default RiskNoteTimelineRecordLayout;
